/* eslint-disable zeffy-linter-frontend/no-classname-prop -- legacy baseline (pre-rule); remove when these are migrated */
import Typography from "@mui/material/Typography";
import classnames from "classnames";

import { useMediaQuery } from "../../../common/hooks/useMediaQuery";
import { DashboardIcon } from "../Icon";
import { useMergeClasses } from "../useMergeClasses";

import { StyledRoot } from "./styles";
import { zeffyOptionLabelClasses, OptionLabelSsrProps } from "./types";

import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { Info } from "@/icons/outlined";

export const DashboardOptionLabelSsr: React.FC<OptionLabelSsrProps> = ({
  alignContent = "default",
  children,
  className,
  classes: externalClasses,
  vibe,
  disabled = false,
  errorMessage,
  helperText,
  label,
  size = "medium",
  tooltipText,
  fullWidth,
  "data-test": dataTest,
  style,
  onClick,
  inputId,
  labelSx,
  forwardedRef,
}) => {
  const { isSmallScreen } = useMediaQuery();
  const classes = useMergeClasses(zeffyOptionLabelClasses, externalClasses);

  return (
    <StyledRoot
      style={style}
      className={classnames(
        classes.container,
        className,
        { [classes.fullWidth]: Boolean(fullWidth) },
        { [classes.clickable]: Boolean(onClick) }
      )}
      ref={forwardedRef}
      data-test={dataTest}
      onClick={onClick}
    >
      <div
        className={classnames(classes.contentSection, {
          [classes.fullWidth]: Boolean(fullWidth),
          [classes.contentSectionCentered]: alignContent === "center" || Boolean(tooltipText),
        })}
      >
        <div className={classnames(classes.children, { [classes.childrenSmall]: size === "small" })}>{children}</div>
        {label && (
          <div
            className={classnames(classes.labelSection, {
              [classes.fullWidth]: Boolean(fullWidth),
            })}
          >
            <Typography
              variant={size === "medium" ? "body1" : "body2"}
              className={classnames(classes.label, externalClasses?.label, {
                [classes.labelForm]: vibe !== "neutral",
                [classes.labelNeutral]: vibe === "neutral",
                [classes.disabledLabel]: disabled,
                [classes.fullWidth]: Boolean(fullWidth),
              })}
              display="inline"
              component="label"
              htmlFor={inputId}
              sx={labelSx}
            >
              {label}
            </Typography>
          </div>
        )}
        {tooltipText && (
          <DashboardTooltip
            title={tooltipText}
            className={classnames(classes.tooltip, { [classes.tooltipSmall]: size === "small" })}
            placement="top"
            enterTouchDelay={isSmallScreen ? 0 : undefined}
          >
            <DashboardIcon size="small" sx={{ cursor: "pointer" }}>
              <Info />
            </DashboardIcon>
          </DashboardTooltip>
        )}
      </div>
      {errorMessage ? (
        <Typography variant="caption" className={classes.error}>
          {errorMessage}
        </Typography>
      ) : helperText ? (
        <Typography variant="caption" className={classes.helperText}>
          {helperText}
        </Typography>
      ) : null}
    </StyledRoot>
  );
};

DashboardOptionLabelSsr.displayName = "DashboardOptionLabel";
